import type { ClientOptions, MaybeOptionalInit } from 'openapi-fetch';
import {
  createFinalURL,
  createQuerySerializer,
  removeTrailingSlash,
} from 'openapi-fetch';
import type {
  HttpMethod,
  PathsWithMethod,
  RequiredKeysOf,
} from 'openapi-typescript-helpers';

// Esse arquivo é muito feio, porém é a única forma confiável de
// gerar urls da API seguindo os padrões da biblioteca que usamos, a `openapi-fetch`.

type InitParam<Init> = RequiredKeysOf<Init> extends never ? [Init?] : [Init];

/**
 * Builder usado para criar urls da API.\
 * Segue os padrões da biblioteca `openapi-fetch`.
 *
 * Útil para criar urls completas para serem usadas fora da biblioteca, como por exemplo
 * para adicionar como href de links.
 * @param options Objeto contendo as opções de configuração do builder, como `baseUrl` e `querySerializer`.
 * @returns Uma função que recebe o método HTTP, o path e os parâmetros da requisição, e retorna a URL completa.
 */
export function createUrlBuilder<Paths extends object>(
  options: Pick<ClientOptions, 'baseUrl' | 'querySerializer'> = {},
) {
  const baseUrl = removeTrailingSlash(options.baseUrl ?? '');
  const querySerializer =
    typeof options.querySerializer === 'function'
      ? options.querySerializer
      : createQuerySerializer(options.querySerializer);

  return function fullUrl<
    Method extends HttpMethod,
    Path extends PathsWithMethod<Paths, Method>,
    Init extends MaybeOptionalInit<Paths[Path], Method & keyof Paths[Path]>,
  >(_method: Method, path: Path, ...init: InitParam<Init>): string {
    const params =
      (init[0] as { params?: Record<string, unknown> } | undefined)?.params ??
      {};
    return createFinalURL(path as string, { baseUrl, params, querySerializer });
  };
}
